import './App.scss';
import Navigation from './navigation';
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { RiFacebookFill, RiInstagramFill, RiTwitterXFill } from 'react-icons/ri';
import { LuLink2 } from 'react-icons/lu';
import SideForm from './components/SideForm';
import { FaEdit } from 'react-icons/fa';
import DynamicMarkerSize from './components/DynamicMarkerSize';

let gurudwaras = require('./gurudwaras.json');

// ✅ Save map position in localStorage when the user moves the map
const SaveMapPosition = () => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem(
        'mapPosition',
        JSON.stringify({ lat: center.lat, lng: center.lng, zoom })
      );
    },
  });
  return null;
};

// ✅ Center and zoom the map only on first load if no saved position exists
const SetViewOnLocation = ({ position, zoom }) => {
  const map = useMap();
  const [hasSetView, setHasSetView] = useState(false);

  useEffect(() => {
    if (position && !hasSetView) {
      map.flyTo(position, zoom, { animate: true, duration: 2 });
      setHasSetView(true);
    }
  }, [position, zoom, map, hasSetView]);

  return null;
};

// ✅ Create dynamic marker icon
const createMarkerIcon = (size) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="width:${size}px; height:${size}px; background: red; border-radius: 50%;"></div>`,
    iconSize: [size, size],
  });
};

const SimpleMap = () => {
  const [position, setPosition] = useState(null);
  const [zoom, setZoom] = useState(3);
  const [markerSize, setMarkerSize] = useState(7);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedGurudwara, setSelectedGurudwara] = useState(null);

  useEffect(() => {
    // ✅ Load saved position from localStorage
    const savedPosition = JSON.parse(localStorage.getItem('mapPosition'));

    if (savedPosition) {
      setPosition([savedPosition.lat, savedPosition.lng]);
      setZoom(savedPosition.zoom);
    } else {
      // ✅ If no saved position, use geolocation
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (location) => {
            setPosition([location.coords.latitude, location.coords.longitude]);
            setZoom(11);
            // ✅ Save initial position in localStorage
            localStorage.setItem(
              'mapPosition',
              JSON.stringify({
                lat: location.coords.latitude,
                lng: location.coords.longitude,
                zoom: 11,
              })
            );
          },
          () => console.warn('Location access denied or unavailable.')
        );
      } else {
        console.warn('Geolocation is not supported.');
      }
    }
  }, []);

  const handleEditClick = (gurudwara) => {
    setSelectedGurudwara(gurudwara);
    setDrawerOpen(true);
  };

  return (
    <>
      <MapContainer
        center={position || [38, 10]}
        zoom={zoom}
        scrollWheelZoom={true}
        style={{ height: '100vh', width: '100vw' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* ✅ Save the map position when user moves */}
        <SaveMapPosition />

        {/* ✅ Center and zoom on first load only */}
        {position && <SetViewOnLocation position={position} zoom={zoom} />}
        <DynamicMarkerSize setMarkerSize={setMarkerSize} />

        {/* ✅ Cluster markers for better performance */}
        <MarkerClusterGroup>
          {gurudwaras.map((country, countryIndex) =>
            country.states.map((state, stateIndex) =>
              state.gurudwaras.map((gurudwara, gurudwaraIndex) => {
                const lat = gurudwara?.info?.address?.latitude;
                const lng = gurudwara?.info?.address?.longitude;

                if (lat && lng) {
                  return (
                    <Marker
                      key={`${countryIndex}-${stateIndex}-${gurudwaraIndex}`}
                      position={[lat, lng]}
                      icon={createMarkerIcon(markerSize)}>
                      <Popup>
                        <div className="popup">
                          <h3>{gurudwara.name}</h3>
                          <p>
                            {gurudwara.info.address.street}, {gurudwara.info.address.city},{' '}
                            {gurudwara.info.address.zipcode}
                          </p>
                          <p>
                            {state.name}, {country.name}
                          </p>
                        </div>
                        <div className="popup-links">
                          {gurudwara.info.website && (
                            <a
                              className="socials"
                              href={gurudwara.info.website}
                              target="_blank"
                              rel="noopener noreferrer">
                              <LuLink2 />
                            </a>
                          )}
                          {gurudwara.info.social.facebook && (
                            <a
                              className="socials"
                              href={gurudwara.info.social.facebook}
                              target="_blank"
                              rel="noopener noreferrer">
                              <RiFacebookFill />
                            </a>
                          )}
                          {gurudwara.info.social.instagram && (
                            <a
                              className="socials"
                              href={gurudwara.info.social.instagram}
                              target="_blank"
                              rel="noopener noreferrer">
                              <RiInstagramFill />
                            </a>
                          )}
                          {gurudwara.info.social.twitter && (
                            <a
                              className="socials"
                              href={gurudwara.info.social.twitter}
                              target="_blank"
                              rel="noopener noreferrer">
                              <RiTwitterXFill />
                            </a>
                          )}
                          <button className="edit-btn" onClick={() => handleEditClick(gurudwara)}>
                            <FaEdit /> Edit
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                }
                return null;
              })
            )
          )}
        </MarkerClusterGroup>
      </MapContainer>

      <SideForm
        title={'Edit Gurudwara Details'}
        isOpen={drawerOpen}
        toggleDrawer={() => setDrawerOpen(false)}
        selectedData={selectedGurudwara}
        onSave={(updatedData) => console.log(updatedData)}
      />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <Navigation />
      <header className="App-header">
        <SimpleMap />
      </header>
    </div>
  );
}

export default App;
